// src/pages/workflow/WorkFlowInstance.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { workflowApi } from "../../Apis/Api"; // Adjust path as needed
import { apiCall } from "../../Apis/apiCall"; // Import apiCall
import useDebounce from "../../hook/useDebounce";
import LoadingComponent from "../../components/ui/LoadingComponent";
import Breadcrumb from "../../components/Breadcrumb";
import TableDataNoAvailable from "../../components/ui/TableDataNoAvailable";
import CardContainer from "../../components/ui/CardContainer";
import PerPageDropdown from "../../components/ui/PerPageDropdown";
import PaginationControls from "../../components/ui/PaginationControls";
import ErrorAlert from "../../components/ui/ErrorAlert";
import SortIcons from "../../components/ui/SortIcons";
import StatusBadge from "../../components/ui/StatusBadge";
import { usePermissions } from "../../context/PermissionContext";
import { CHECK_IN_PHOTO } from "../../utils/Contants";
import AttendanceOverlay from "../../components/home/AttendanceOverlay";
import { Button } from "react-bootstrap"; // Added for Button component
import { Printer } from "lucide-react";
import TicketComponent from "../../components/workflow/TicketComponent";
import ReceiptComponent from "../../components/workflow/ReceiptComponent";
import StepHistoryModal from "../../components/workflow/StepHistoryModal";

const WorkFlowInstance = () => {
  const [instances, setInstances] = useState([]);
  const [isContinuing, setIsContinuing] = useState({});
  const [isCanceling, setIsCanceling] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [instanceToCancel, setInstanceToCancel] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
  });
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });

  const navigate = useNavigate();
  const debouncedSearch = useDebounce(filters.search, 500);

  const searchInputRef = useRef(null);
  const { hasPermission, isAttendanceMarked, refreshAuthStatus } =
    usePermissions();

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  useEffect(() => {
    fetchInstances();
  }, [pagination.currentPage, pagination.perPage, debouncedSearch, sortConfig]);

  const fetchInstances = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearch,
        ...(sortConfig.field && {
          sort_by: sortConfig.field,
          sort_order: sortConfig.direction,
        }),
      };

      const response = await workflowApi.getMyInstances(params);

      if (!response.success) {
        throw new Error(
          response.message ||
            response.error ||
            "Failed to fetch workflow instances"
        );
      }

      if (!Array.isArray(response.data)) {
        throw new Error(
          response.message || "Invalid API response: instances data missing"
        );
      }

      const data = response.data.map((instance, index) => {
        let stepCount = instance.step_history?.length || 0;
        if (instance.current_step) {
          const currentStepIndex = instance.step_history?.findIndex(
            (step) => step.step_label === instance.current_step
          );
          stepCount =
            currentStepIndex >= 0
              ? currentStepIndex + 1
              : (instance.step_history?.length || 0) + 1;
        }

        const isCompleted =
          instance.steps_remaining === 0 && instance.steps_filled > 0;
        const actualStatus = isCompleted ? "completed" : instance.status;

        const vehicleData = extractVehicleAndParkingData(instance.step_history);

        return {
          id: instance.instance_id || index + 1,
          workflow_name: instance.workflow_name || "Unknown Workflow",
          workflow_id: instance.workflow_id || "-",
          current_step_label: isCompleted
            ? "Completed"
            : instance.current_step || "-",
          current_step_code: isCompleted
            ? "COMPLETED"
            : instance.current_step || "-",
          step_count: stepCount,
          status: actualStatus.charAt(0).toUpperCase() + actualStatus.slice(1),
          status_label: actualStatus || "-",
          assigned_to: instance.assign_status || "Unassigned",
          assigned_by: instance.assigned_by || "-",
          created_at: instance.created_at
            ? new Date(instance.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "-",
          raw_created_at: instance.created_at || "-",
          step_history: instance.step_history || [],
          location_id: instance.location_id || null,
          steps_filled: instance.steps_filled || 0,
          steps_remaining: instance.steps_remaining || 0,
          total_steps: instance.total_steps || 0,
          vehicle_data: vehicleData,
          step_picked: instance.step_picked || false,
        };
      });

      setInstances(data);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.pagination?.current_page || 1,
        perPage: response.pagination?.per_page || 10,
        total: response.pagination?.total || data.length,
      }));
    } catch (err) {
      const errorMessage = err.message || "Failed to fetch workflow instances.";
      setError(errorMessage);
      setInstances([]);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleViewDetails = (instance) => {
    setSelectedInstance(instance);
    setShowModal(true);
  };

  const handleContinue = async (instance) => {
    if (isContinuing[instance.id]) return;
    if (!instance?.id || !instance?.workflow_id) {
      toast.error("Cannot continue workflow. Invalid instance data.");
      return;
    }

    setIsContinuing((prev) => ({ ...prev, [instance.id]: true }));

    try {
      const stepResponse = await workflowApi.getWorkflowStep(instance.id);
      const responseData = stepResponse?.data || stepResponse;

      const stepId =
        responseData?.new_step_id ||
        responseData?.step_id ||
        responseData?.id ||
        responseData?.step_code;

      if (!stepId) {
        throw new Error(
          "Invalid step response: Missing step_id or new_step_id."
        );
      }

      if (responseData?.isAssigned === "N") {
        toast.info("This workflow step is assigned to another user.");
        setIsContinuing((prev) => ({ ...prev, [instance.id]: false }));
        return;
      }

      const lockStepPayload = {
        workflow_instance_id: instance.id,
        workflow_step_id: stepId,
      };

      const lockStepResponse = await apiCall(
        "POST",
        "/workflow-instance/lock-step",
        lockStepPayload,
        navigate
      );

      if (!lockStepResponse.data.success) {
        throw new Error(
          lockStepResponse.data.message || "Failed to lock workflow step."
        );
      }

      navigate(`/workflows-running`, {
        state: {
          instanceId: instance.id,
          workflowId: instance.workflow_id,
          workflowName: instance.workflow_name,
          stepCount: instance.step_count,
          locationId: instance.location_id || null,
        },
      });
    } catch (error) {
      toast.error(error.message || "Failed to continue workflow.");
    } finally {
      setIsContinuing((prev) => ({ ...prev, [instance.id]: false }));
    }
  };

  const handleCancel = (instance) => {
    setInstanceToCancel(instance);
    setShowCancelConfirmModal(true);
  };

  const confirmCancel = async () => {
    if (!instanceToCancel?.id) {
      toast.error("Cannot cancel workflow. Invalid instance data.");
      setShowCancelConfirmModal(false);
      return;
    }

    setIsCanceling((prev) => ({ ...prev, [instanceToCancel.id]: true }));

    try {
      const response = await apiCall(
        "POST",
        `/cancel/${instanceToCancel.id}/instance`,
        {},
        navigate
      );

      if (response.data.success) {
        toast.success("Workflow instance canceled successfully!");
        fetchInstances();
      } else {
        throw new Error(
          response.data.message || "Failed to cancel workflow instance"
        );
      }
    } catch (err) {
      const errorMessage = err.message || "Failed to cancel workflow instance.";
      toast.error(errorMessage);
    } finally {
      setIsCanceling((prev) => ({ ...prev, [instanceToCancel.id]: false }));
      setShowCancelConfirmModal(false);
      setInstanceToCancel(null);
    }
  };

  const handleCloseCancelModal = () => {
    setShowCancelConfirmModal(false);
    setInstanceToCancel(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInstance(null);
  };

  const extractVehicleAndParkingData = (stepHistory) => {
    if (!Array.isArray(stepHistory)) return {};

    const result = {
      vehicle_license_plate: null,
      vehicle_make: null,
      vehicle_model: null,
      vehicle_color: null,
      parking_ticket: null,
      parking_slot_number: null,
      location_name: null,
      parked_time: null,
      front_image: null,
      back_image: null,
      left_image: null,
      right_image: null,
      _image_base_url: CHECK_IN_PHOTO,
    };

    stepHistory.forEach((step) => {
      const data = step?.submitted_data;
      if (!data || typeof data !== "object") return;

      const getFirstImage = (img) => {
        if (!img) return null;
        return Array.isArray(img) ? img[0] : img;
      };

      if (data.parked_time && !result.parked_time) {
        result.parked_time = data.parked_time;
      }
      if (data.location_name && !result.location_name) {
        result.location_name = data.location_name;
      }

      const vehicleFields = [
        "vehicle_license_plate",
        "vehicle_make",
        "vehicle_model",
        "vehicle_color",
      ];

      vehicleFields.forEach((field) => {
        if (data[field] && !result[field]) {
          result[field] = data[field];
        }
      });

      if (data.parking_ticket && !result.parking_ticket) {
        result.parking_ticket = data.parking_ticket;
      } else if (data.ticket_number && !result.parking_ticket) {
        result.parking_ticket = data.ticket_number;
      }

      if (data.parking_slot_number && !result.parking_slot_number) {
        result.parking_slot_number = data.parking_slot_number;
      } else if (data.parking_slot_id && !result.parking_slot_number) {
        result.parking_slot_number = `Slot ${data.parking_slot_id}`;
      }

      const imageFields = [
        "front_image",
        "back_image",
        "left_image",
        "right_image",
      ];

      imageFields.forEach((field) => {
        if (data[field] && !result[field]) {
          result[field] = getFirstImage(data[field]);
        }
      });
    });

    return result;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const handleLogout = async () => {
    localStorage.clear();
    await refreshAuthStatus();
    // toast.success("Logout Successful");
    navigate("/sign-in", { replace: true });
  };

  const handleAttendanceSuccess = () => {
    refreshAuthStatus();
  };

  // If attendance is not marked, show only the AttendanceOverlay
  if (!isAttendanceMarked) {
    return (
      <AttendanceOverlay
        show={true}
        onClose={() => navigate(-1)} // No-op close handler to prevent closing without attendance
        onLogout={handleLogout}
        onSuccess={handleAttendanceSuccess}
      />
    );
  }

  const renderSubmittedData = (data, depth = 0, fieldName = "") => {
    if (!data || typeof data !== "object") {
      return <span>{data?.toString() || "-"}</span>;
    }

    // Handle ticket_summary case
    if (fieldName === "ticket_summary") {
      return <TicketComponent data={data} fieldName={fieldName} />;
    }

    // Handle receipt case
    if (fieldName === "reciept_image" || fieldName === "receipt") {
      return <ReceiptComponent data={data} fieldName={fieldName} />;
    }

    if (Array.isArray(data)) {
      return (
        <ul className="list-unstyled ms-3">
          {data.map((item, idx) => (
            <li key={idx}>{renderSubmittedData(item, depth + 1)}</li>
          ))}
        </ul>
      );
    }

    return (
      <ul className={`list-unstyled ${depth > 0 ? "ms-3" : ""}`}>
        {Object.entries(data).map(([key, value]) => {
          const isImageField =
            key.toLowerCase().includes("image") ||
            key.toLowerCase().includes("photo") ||
            key.toLowerCase().includes("receipt_image");

          const displayKey = key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());

          let displayValue;
          if (value === null || value === undefined) {
            displayValue = "-";
          } else if (typeof value === "object") {
            displayValue = renderSubmittedData(value, depth + 1, key);
          } else {
            displayValue = value.toString();
          }

          return (
            <li key={key} className="mb-2 d-flex gap-2 align-items-start">
              <strong className="text-capitalize">{displayKey}:</strong>
              {isImageField && typeof value === "string" && value ? (
                <div className="image-container">
                  <img
                    src={`${CHECK_IN_PHOTO}/${value}`}
                    alt={key}
                    className="img-thumbnail"
                    style={{
                      maxWidth: "150px",
                      maxHeight: "150px",
                    }}
                    onClick={() =>
                      window.open(`${CHECK_IN_PHOTO}/${value}`, "_blank")
                    }
                  />
                </div>
              ) : isImageField && Array.isArray(value) && value.length > 0 ? (
                <div className="d-flex gap-2 flex-wrap">
                  {value.map((img, idx) => (
                    <img
                      key={idx}
                      src={`${CHECK_IN_PHOTO}/${img}`}
                      alt={`${key}-${idx}`}
                      className="img-thumbnail"
                      style={{
                        maxWidth: "150px",
                        maxHeight: "150px",
                      }}
                      onClick={() =>
                        window.open(`${CHECK_IN_PHOTO}/${img}`, "_blank")
                      }
                    />
                  ))}
                </div>
              ) : (
                <div>{displayValue}</div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchInstances} />;
  }
  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="navbar-search flex-grow-1">
            <input
              type="text"
              className="form-control h-40-px w-100"
              name="search"
              placeholder="Search by Workflow Name"
              value={filters.search}
              onChange={handleSearchChange}
              ref={searchInputRef}
              aria-label="Search workflows by name"
            />
            <i className="bi bi-search icon"></i>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Workflow Instances" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {(instances.length === 0 || !isAttendanceMarked) && !loading ? (
            <TableDataNoAvailable
              message={
                filters.search
                  ? "No workflows match your search."
                  : "No workflow instances available."
              }
            />
          ) : (
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Vehicle</th>
                  <th scope="col">Details</th>
                  {/* <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("status")}
                    role="button"
                    aria-label={`Sort by status, currently sorted ${sortConfig.field === "status"
                        ? sortConfig.direction
                        : "none"
                      }`}
                  >
                    <div className="d-flex align-items-center justify-content-center">
                      Status
                      <SortIcons field="status" sortConfig={sortConfig} />
                    </div>
                  </th> */}
                  {/* <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("raw_created_at")}
                    role="button"
                    aria-label={`Sort by created date, currently sorted ${sortConfig.field === "raw_created_at"
                        ? sortConfig.direction
                        : "none"
                      }`}
                  >
                    <div className="d-flex align-items-center">
                      Created Date
                      <SortIcons
                        field="raw_created_at"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th> */}
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {instances.map((instance, index) => (
                  <tr key={instance.id}>
                    <td>
                      {(pagination.currentPage - 1) * pagination.perPage +
                        index +
                        1}
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <img
                          src={
                            instance.vehicle_data.front_image ||
                            instance.vehicle_data.back_image ||
                            instance.vehicle_data.left_image ||
                            instance.vehicle_data.right_image
                              ? `${CHECK_IN_PHOTO}/${
                                  instance.vehicle_data.front_image ||
                                  instance.vehicle_data.back_image ||
                                  instance.vehicle_data.left_image ||
                                  instance.vehicle_data.right_image
                                }`
                              : "/car-img.jpg"
                          }
                          alt="Vehicle"
                          className="rounded object-cover"
                          width={48}
                          height={48}
                        />
                        <div>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {instance.vehicle_data.vehicle_make ||
                            instance.vehicle_data.vehicle_model
                              ? `${instance.vehicle_data.vehicle_make || ""}${
                                  instance.vehicle_data.vehicle_make &&
                                  instance.vehicle_data.vehicle_model
                                    ? " - "
                                    : ""
                                }${instance.vehicle_data.vehicle_model || ""}`
                              : "Resume your instance"}
                          </span>
                          <small className="d-block text-neutral-900">
                            Plate:{" "}
                            {instance.vehicle_data.vehicle_license_plate ||
                              "Resume"}
                          </small>
                           {
                                 instance?.step_picked && <div><span className="badge bg-success text-light border px-3 py-1 rounded-pill fw-medium">
                                    Step picked
                                  </span></div>
                                }
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column small">
                        <span className="text-truncate">
                          {[
                            instance.vehicle_data.location_name,
                            instance.vehicle_data.parking_slot_number,
                            instance.vehicle_data.vehicle_color,
                          ]
                            .filter(Boolean)
                            .join(" | ") || "resume"}
                        </span>
                        <span>
                          {instance.vehicle_data.parked_time && (
                            <>
                              Parked:{" "}
                              {instance.vehicle_data.parked_time
                                ? formatDate(instance.vehicle_data.parked_time)
                                : "-"}
                            </>
                          )}
                        </span>
                      </div>
                    </td>
                    {/* <td className="text-center">
                      <StatusBadge status={instance.status} />
                    </td> */}
                    {/* <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {instance.created_at}
                      </span>
                    </td> */}
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("view_workflow_instance") &&
                          (instance.status === "In_progress" ||
                            instance.status === "Completed") && (
                            <button
                              type="button"
                              onClick={() => handleContinue(instance)}
                              className={`fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle ${
                                instance.status === "Completed"
                                  ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                                  : "bg-success-focus text-success-600 bg-hover-success-200"
                              }`}
                              disabled={
                                isContinuing[instance.id] ||
                                instance.status === "Completed"
                              }
                              title={
                                instance.status === "Completed"
                                  ? "Workflow already completed"
                                  : "Continue workflow"
                              }
                              aria-label={
                                instance.status === "Completed"
                                  ? "Workflow completed"
                                  : `Continue workflow ${instance.workflow_name}`
                              }
                            >
                              {isContinuing[instance.id] &&
                              instance.status === "In_progress" ? (
                                <div
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                              ) : (
                                <Icon
                                  icon="bi:play-fill"
                                  className="menu-icon text-xl"
                                />
                              )}
                            </button>
                          )}
                        {hasPermission("view_workflow_instance") &&
                          instance.status === "In_progress" && (
                            <button
                              type="button"
                              onClick={() => handleCancel(instance)}
                              className="fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle bg-danger-focus text-danger-600 bg-hover-danger-200"
                              disabled={isCanceling[instance.id]}
                              title="Cancel workflow"
                              aria-label={`Cancel workflow ${instance.workflow_name}`}
                            >
                              {isCanceling[instance.id] ? (
                                <div
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Canceling...
                                  </span>
                                </div>
                              ) : (
                                <Icon
                                  icon="bi:x-circle-fill"
                                  className="menu-icon text-xl"
                                />
                              )}
                            </button>
                          )}
                        {hasPermission("view_workflow_instance") && (
                          <button
                            type="button"
                            onClick={() => handleViewDetails(instance)}
                            className="fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle bg-primary-600 text-white bg-hover-primary-700"
                            title="View Details"
                            aria-label={`View details of workflow ${instance.workflow_name}`}
                          >
                            <Icon
                              icon="bi:eye-fill"
                              className="menu-icon text-xl"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* Step History Modal */}
      <StepHistoryModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedInstance(null);
        }}
        instance={selectedInstance}
        title="Instance History"
      />

      {/* Cancel Confirmation Modal */}
      {showCancelConfirmModal && instanceToCancel && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Cancellation</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseCancelModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to cancel the workflow instance{" "}
                  <strong>{instanceToCancel.workflow_name}</strong>
                </p>
                <p className="text-neutral-900 small">
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseCancelModal}
                  aria-label="Cancel cancellation"
                  disabled={isCanceling[instanceToCancel.id]}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmCancel}
                  aria-label={`Confirm cancel workflow ${instanceToCancel.workflow_name}`}
                  disabled={isCanceling[instanceToCancel.id]}
                >
                  {isCanceling[instanceToCancel.id]
                    ? // <>
                      //   <div
                      //     className="spinner-border spinner-border-sm me-2"
                      //     role="status"
                      //     aria-hidden="true"
                      //   ></div>
                      // </>
                      "Please wait..."
                    : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCancelConfirmModal && <div className="no-modal fade show"></div>}
    </>
  );
};

export default WorkFlowInstance;
